/**
 * Finance360 Education — parent finance portal.
 * Consolidated statement, ageing, receipts, next due — for a parent's children.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Wallet, ClipboardList, Receipt, Download, TrendingDown, AlertCircle, ChevronRight } from "lucide-react";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, KPITile, Section, Loading, StatusPill } from "../components/Common";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function ParentFinance() {
  const { school } = useParams();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [statement, setStatement] = useState(null);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, b] = await Promise.all([
        client.get("/finance360/parent/summary"),
        client.get("/finance360/fee-bundles"),
      ]);
      setSummary(s.data);
      setBundles(b.data.bundles || []);
      if ((s.data.children || []).length > 0) {
        setSelectedChild(s.data.children[0].student.id);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedChild) return;
    client.get(`/finance360/statement/${selectedChild}`).then(({ data }) => setStatement(data));
  }, [selectedChild]);

  if (!summary) return <Loading label="Loading finance portal…" />;

  const children = summary.children || [];
  const activeChild = children.find((c) => c.student.id === selectedChild);

  return (
    <div data-testid="parent-finance-page">
      <PageHeader
        kicker="Finance360™ · Family Portal"
        title={`${(user?.full_name || "Guardian").replace(/^(Mr|Mrs|Ms|Dr|Prof)\.?\s+/i, "").split(" ")[0] || "Guardian"}, here is your family account`}
        subtitle="Consolidated statement, receipts and fee bundles. All amounts in USD."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="parent-finance-kpis">
        <KPITile tone="dark" testId="pf-billed" label="Total billed" value={money(summary.total_billed_usd)} />
        <KPITile testId="pf-paid" label="Total paid" value={money(summary.total_paid_usd)} sub={`${children.length} child(ren)`} />
        <KPITile testId="pf-out" label="Outstanding" value={money(summary.total_outstanding_usd)} accent={summary.total_outstanding_usd > 0 ? "Action required" : "Settled"} />
        <KPITile testId="pf-children" label="Statements" value={children.length} sub="Live account records" />
      </div>

      {/* Children picker */}
      {children.length > 0 && (
        <div className="hl-card p-4 mb-6" data-testid="child-picker">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Select a child</div>
          <div className="flex flex-wrap gap-2">
            {children.map((c) => (
              <button
                key={c.student.id}
                onClick={() => setSelectedChild(c.student.id)}
                data-testid={`pf-child-${c.student.id}`}
                className={`text-xs px-3 py-2 rounded border transition ${selectedChild === c.student.id ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white border-slate-200 hover:border-slate-300"}`}
              >
                {c.student.first_name} {c.student.last_name} · {c.student.year_group}
                <span className="ml-2 text-[10px] opacity-70">{money(c.outstanding_usd)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Selected child statement */}
      {statement && (
        <>
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="hl-card p-5 lg:col-span-2" data-testid="statement-header">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="overline">Statement · {statement.student.first_name} {statement.student.last_name}</div>
                  <div className="font-display font-bold text-lg">Running ledger</div>
                </div>
                <div className="text-xs text-slate-500">Currency · USD</div>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <MiniStat label="Billed" value={money(statement.statement.total_billed_usd)} />
                <MiniStat label="Paid" value={money(statement.statement.total_paid_usd)} tone="ok" />
                <MiniStat label="Outstanding" value={money(statement.statement.outstanding_usd)} tone={statement.statement.outstanding_usd > 0 ? "warn" : "ok"} />
              </div>
              <div className="overflow-x-auto">
                <table className="hl-table w-full text-sm" data-testid="statement-ledger">
                  <thead>
                    <tr>
                      <th>Date</th><th>Reference</th><th>Description</th>
                      <th className="text-right">Debit</th><th className="text-right">Credit</th>
                      <th className="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statement.statement.ledger.map((row, i) => (
                      <tr key={i}>
                        <td className="text-xs mono">{row.date}</td>
                        <td className="text-xs mono text-slate-600">{(row.reference || "").slice(0, 12)}</td>
                        <td className="text-xs">{row.description}</td>
                        <td className="text-right text-xs mono">{row.type === "invoice" ? money(row.amount) : ""}</td>
                        <td className="text-right text-xs mono text-emerald-600">{row.type === "receipt" ? money(Math.abs(row.amount)) : ""}</td>
                        <td className="text-right text-xs mono font-semibold" style={{ color: row.balance > 0 ? "#dc2626" : "#059669" }}>{money(row.balance)}</td>
                      </tr>
                    ))}
                    {!statement.statement.ledger.length && (
                      <tr><td colSpan={6} className="text-center py-6 text-slate-400">No transactions on file</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="hl-card p-5" data-testid="ageing-card">
              <div className="overline mb-2">Ageing analysis</div>
              <div className="h-52">
                <ResponsiveContainer>
                  <BarChart data={statement.statement.ageing}>
                    <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip formatter={(v) => money(v)} />
                    <Bar dataKey="amount" fill="#D9531E" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {activeChild?.next_due && (
                <div className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Next due: <span className="mono font-semibold">{activeChild.next_due}</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Section title="Invoices" subtitle="Termly billing statements">
              <div className="hl-card overflow-hidden">
                <table className="hl-table w-full text-sm" data-testid="invoices-list">
                  <thead><tr><th>Term</th><th>Total</th><th>Paid</th><th>Status</th><th>Due</th></tr></thead>
                  <tbody>
                    {statement.statement.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="text-xs">{inv.term} {inv.academic_year}</td>
                        <td className="mono">{money(inv.total)}</td>
                        <td className="mono text-emerald-700">{money(inv.amount_paid)}</td>
                        <td><StatusPill status={inv.status} /></td>
                        <td className="text-xs mono">{inv.due_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Receipts" subtitle="Payments received">
              <div className="hl-card overflow-hidden">
                <table className="hl-table w-full text-sm" data-testid="receipts-list">
                  <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th className="text-right">Amount</th></tr></thead>
                  <tbody>
                    {statement.statement.receipts.map((r) => (
                      <tr key={r.id}>
                        <td className="text-xs mono">{(r.received_on || "").slice(0, 10)}</td>
                        <td className="text-xs capitalize">{r.method}</td>
                        <td className="text-xs mono text-slate-600">{r.reference}</td>
                        <td className="text-right mono font-semibold text-emerald-700">{money(r.amount)}</td>
                      </tr>
                    ))}
                    {!statement.statement.receipts.length && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-400">No receipts yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        </>
      )}

      <Section title="Fee catalogue" subtitle="Cambridge, ZIMSEC and school-specific fee bundles (advisory)">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fee-bundles-grid">
          {bundles.map((b) => (
            <div key={b.code} className="hl-card p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)] font-semibold">{b.board}</div>
                  <div className="font-semibold mt-0.5">{b.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 capitalize">{b.category}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-lg">{money(b.amount_usd)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{b.term_billable ? "per term" : "one-off"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center" data-testid="finance-source-label">
        Powered by Education360™ Finance360 · Central Metrics Service
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const color = tone === "ok" ? "#059669" : tone === "warn" ? "#dc2626" : "#0f172a";
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-bold text-xl mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
